import type { ReactNode } from 'react'
import cx from 'classnames'
import { TabBar } from '@/components/TabBar'
import styles from './Inspector.module.css'

export interface InspectorProps {
  children?: ReactNode
  testId?: string
}

/**
 * The panel along the right edge that edits the selected item.
 */
export function Inspector({ children, testId }: InspectorProps) {
  return (
    <aside className={styles.root} data-test={testId}>
      {children}
    </aside>
  )
}

export interface InspectorHeaderProps {
  /**
   * What is being edited, like the selected element's ID.
   */
  title?: ReactNode
  /**
   * `TabItem`s.
   */
  tabs?: ReactNode
}

/**
 * The inspector's title, above its tabs.
 */
export function InspectorHeader({ title, tabs }: InspectorHeaderProps) {
  return (
    <div className={styles.header}>
      {title && <div className={styles.title}>{title}</div>}
      {tabs && (
        <div className={styles.tabs}>
          <TabBar align="left">{tabs}</TabBar>
        </div>
      )}
    </div>
  )
}

export interface InspectorContentProps {
  children?: ReactNode
}

/**
 * The inspector's scrolling body.
 */
export function InspectorContent({ children }: InspectorContentProps) {
  return <div className={styles.content}>{children}</div>
}

export interface InspectorSectionProps {
  children?: ReactNode
  title?: string
  /**
   * Lets the content reach the inspector's edges, like an expandable list.
   */
  isFlush?: boolean
}

/**
 * A group of fields in the inspector.
 */
export function InspectorSection({
  children,
  title,
  isFlush,
}: InspectorSectionProps) {
  return (
    <div className={cx(styles.section, isFlush && styles.flush)}>
      {title && <div className={styles.sectionTitle}>{title}</div>}
      {children}
    </div>
  )
}
